import React, { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState } from 'react';

import type { TicketType } from '../../api/tickets/types';

// Lazy: the dialog pulls in the tickets API and a fair amount of MUI, and most sessions never open it.
const ReportIssueDialog = lazy(() => import('./ReportIssueDialog'));

type ReportIssueContextValue = { openReportIssue: (type: TicketType) => void };

const ReportIssueContext = createContext<ReportIssueContextValue>({ openReportIssue: () => {} });

/**
 * Holds the single report dialog for the whole app.
 *
 * There is more than one way in — the header Feedback menu and the user menu — and both must drive the
 * SAME dialog. Mounting a dialog per trigger would give each its own draft state, so a report started
 * from one entry point would vanish if the user reached for the other.
 *
 * The ticket type is chosen at the entry point rather than inside the dialog, so it is passed in here.
 */
export const ReportIssueProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [openType, setOpenType] = useState<TicketType | null>(null);

    const openReportIssue = useCallback((type: TicketType) => setOpenType(type), []);
    const handleClose = useCallback(() => setOpenType(null), []);

    const value = useMemo(() => ({ openReportIssue }), [openReportIssue]);

    return (
        <ReportIssueContext.Provider value={value}>
            {children}
            {openType && (
                <Suspense fallback={null}>
                    <ReportIssueDialog open type={openType} onClose={handleClose} />
                </Suspense>
            )}
        </ReportIssueContext.Provider>
    );
};

/** Returns `openReportIssue(type)`. Safe to call outside the provider — it is then a no-op. */
export function useReportIssue(): ReportIssueContextValue {
    return useContext(ReportIssueContext);
}

export default ReportIssueContext;
